import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./Dialog";
import { Button } from "./Button";

type ConfirmOpts = {
  title: string;
  message?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: "default" | "danger";
};

type AlertOpts = {
  title: string;
  message?: React.ReactNode;
  buttonLabel?: string;
};

type Entry =
  | { kind: "confirm"; opts: ConfirmOpts; resolve: (v: boolean) => void }
  | { kind: "alert"; opts: AlertOpts; resolve: () => void };

type Ctx = {
  confirm: (opts: ConfirmOpts) => Promise<boolean>;
  alert: (opts: AlertOpts) => Promise<void>;
};

const ConfirmCtx = React.createContext<Ctx | null>(null);

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [entry, setEntry] = React.useState<Entry | null>(null);

  const confirm = React.useCallback(
    (opts: ConfirmOpts) =>
      new Promise<boolean>((resolve) => {
        setEntry({ kind: "confirm", opts, resolve });
      }),
    []
  );

  const alertFn = React.useCallback(
    (opts: AlertOpts) =>
      new Promise<void>((resolve) => {
        setEntry({ kind: "alert", opts, resolve });
      }),
    []
  );

  const handleCancel = React.useCallback(() => {
    if (!entry) return;
    if (entry.kind === "confirm") entry.resolve(false);
    else entry.resolve();
    setEntry(null);
  }, [entry]);

  const handleConfirm = React.useCallback(() => {
    if (!entry) return;
    if (entry.kind === "confirm") entry.resolve(true);
    else entry.resolve();
    setEntry(null);
  }, [entry]);

  const value = React.useMemo<Ctx>(
    () => ({ confirm, alert: alertFn }),
    [confirm, alertFn]
  );

  const open = entry !== null;
  const isDanger = entry?.kind === "confirm" && entry.opts.tone === "danger";
  const confirmLabel =
    entry?.kind === "confirm"
      ? entry.opts.confirmLabel ?? (isDanger ? "Confirm" : "OK")
      : entry?.opts.buttonLabel ?? "OK";
  const cancelLabel =
    entry?.kind === "confirm" ? entry.opts.cancelLabel ?? "Cancel" : null;

  return (
    <ConfirmCtx.Provider value={value}>
      {children}
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) handleCancel();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{entry?.opts.title ?? ""}</DialogTitle>
            {entry?.opts.message !== undefined && (
              <DialogDescription>{entry.opts.message}</DialogDescription>
            )}
          </DialogHeader>
          <DialogFooter className="mt-6 gap-2">
            {cancelLabel !== null && (
              <Button variant="outline" onClick={handleCancel}>
                {cancelLabel}
              </Button>
            )}
            <Button
              variant={isDanger ? "destructive" : "default"}
              onClick={handleConfirm}
              autoFocus
            >
              {confirmLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmCtx.Provider>
  );
}

function useCtx(): Ctx {
  const ctx = React.useContext(ConfirmCtx);
  if (!ctx) {
    throw new Error("useConfirm/useAlert must be used inside <ConfirmProvider>");
  }
  return ctx;
}

export function useConfirm() {
  return useCtx().confirm;
}

export function useAlert() {
  return useCtx().alert;
}
